import { generateExchangeInstIds, normalizeSymbolInput } from '@/lib/exchangeInstId';
import type { PositionGroupView } from '@/services/manualTradingApi';
import type { RealtimeSpreadDirection } from '@/services/oppDetectorApi';

export type PositionGroupChartConfig = {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  direction: RealtimeSpreadDirection;
};

function toUiExchange(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return value;
}

function groupSymbol(g: PositionGroupView): string {
  return (g.long_position?.symbol ?? g.short_position?.symbol ?? '').trim();
}

/** group_id: arb_{long_exchange}_{short_exchange}_{symbol}_{ts_ms} */
function chartConfigFromGroupId(groupId: string): PositionGroupChartConfig | null {
  const parts = groupId.split('_');
  if (parts.length < 4 || parts[0] !== 'arb') return null;
  const [, longExchange, shortExchange, symbol] = parts;
  if (!longExchange || !shortExchange || !symbol) return null;
  const normalizedSymbol = normalizeSymbolInput(symbol);
  const exchange1 = toUiExchange(longExchange);
  const exchange2 = toUiExchange(shortExchange);
  const generated = generateExchangeInstIds(exchange1, exchange2, normalizedSymbol);
  return {
    symbol: normalizedSymbol,
    exchange1,
    exchange2,
    instId1: generated.instId1,
    instId2: generated.instId2,
    direction: 'long1_short2',
  };
}

/** Derive RealtimeSpread chart config from a position group (long → exchange1, short → exchange2). */
export function chartConfigFromPositionGroup(g: PositionGroupView): PositionGroupChartConfig | null {
  const longPos = g.long_position;
  const shortPos = g.short_position;
  if (longPos && shortPos) {
    if (longPos.exchange === shortPos.exchange) {
      return chartConfigFromGroupId(g.group_id);
    }
    const symbol = groupSymbol(g);
    if (symbol) {
      const normalizedSymbol = normalizeSymbolInput(symbol);
      const exchange1 = toUiExchange(longPos.exchange);
      const exchange2 = toUiExchange(shortPos.exchange);
      const generated = generateExchangeInstIds(exchange1, exchange2, normalizedSymbol);
      return {
        symbol: normalizedSymbol,
        exchange1,
        exchange2,
        instId1: longPos.inst_id.trim() || generated.instId1,
        instId2: shortPos.inst_id.trim() || generated.instId2,
        direction: 'long1_short2',
      };
    }
  }
  return chartConfigFromGroupId(g.group_id);
}
